import React from 'react';
import { Plus, Trash2, GripVertical, Lock } from 'lucide-react';
import Input from './Input';
import CustomSelect from './CustomSelect';
import {
  QUESTION_TYPES,
  newCustomField,
  isSystemField,
} from '../../utils/leadFormFields';

/**
 * Editable questionnaire schema (same UX as internal ConsultationSettings).
 * fields / onChange: array of { id, type, label, required, options?, placeholder?, system? }
 */
const QuestionSchemaEditor = ({
  fields = [],
  onChange,
  maxFields = 16,
  title = 'Form fields',
  description = 'Choose what clients fill in on your share form. Reorder by editing the list — system fields stay mapped to your booking data.',
}) => {
  const update = (index, key, value) => {
    const next = fields.map((f, i) => (i === index ? { ...f, [key]: value } : f));
    onChange(next);
  };

  const remove = (index) => {
    const field = fields[index];
    if (field?.id === 'guest_name') return; // always keep name
    onChange(fields.filter((_, i) => i !== index));
  };

  const add = () => {
    if (fields.length >= maxFields) return;
    onChange([...fields, newCustomField()]);
  };

  const addOption = (qIndex) => {
    const next = [...fields];
    next[qIndex] = {
      ...next[qIndex],
      options: [...(next[qIndex].options || []), ''],
    };
    onChange(next);
  };

  const updateOption = (qIndex, optIndex, value) => {
    const next = [...fields];
    const opts = [...(next[qIndex].options || [])];
    opts[optIndex] = value;
    next[qIndex] = { ...next[qIndex], options: opts };
    onChange(next);
  };

  const removeOption = (qIndex, optIndex) => {
    const next = [...fields];
    next[qIndex] = {
      ...next[qIndex],
      options: (next[qIndex].options || []).filter((_, i) => i !== optIndex),
    };
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-ink">{title}</h4>
        {description ? (
          <p className="mt-1 text-xs text-ink-secondary">{description}</p>
        ) : null}
      </div>

      <div className="space-y-3">
        {fields.map((q, idx) => {
          const locked = q.id === 'guest_name';
          const system = isSystemField(q);
          return (
            <div
              key={q.id || idx}
              className="rounded-lg border border-border bg-surface-muted/60 p-3 sm:p-4"
            >
              <div className="flex items-start gap-2">
                <GripVertical className="mt-2 h-4 w-4 shrink-0 text-ink-tertiary" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {system ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2 py-0.5 text-[10px] font-semibold uppercase text-ink-tertiary">
                        <Lock className="h-3 w-3" />
                        Built-in
                      </span>
                    ) : (
                      <span className="rounded-full border border-border bg-surface px-2 py-0.5 text-[10px] font-semibold uppercase text-ink-tertiary">
                        Custom
                      </span>
                    )}
                  </div>
                  <Input
                    placeholder="Field label"
                    value={q.label || ''}
                    onChange={(e) => update(idx, 'label', e.target.value)}
                    size="sm"
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="min-w-[140px]">
                      <CustomSelect
                        options={QUESTION_TYPES}
                        value={q.type || 'text'}
                        onChange={(value) => update(idx, 'type', value)}
                        placeholder="Type"
                        size="sm"
                        disabled={system}
                      />
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-ink-secondary">
                      <input
                        type="checkbox"
                        checked={!!q.required}
                        disabled={locked}
                        onChange={(e) => update(idx, 'required', e.target.checked)}
                      />
                      Required
                    </label>
                  </div>
                  {q.type === 'select' ? (
                    <div className="space-y-1">
                      <span className="text-xs text-ink-tertiary">Options</span>
                      {(q.options || []).map((opt, oi) => (
                        <div key={oi} className="flex gap-1">
                          <Input
                            value={opt}
                            onChange={(e) => updateOption(idx, oi, e.target.value)}
                            placeholder={`Option ${oi + 1}`}
                            size="sm"
                          />
                          <button
                            type="button"
                            onClick={() => removeOption(idx, oi)}
                            className="rounded p-1 text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addOption(idx)}
                        className="text-xs font-medium text-ink underline-offset-2 hover:underline"
                      >
                        + Add option
                      </button>
                    </div>
                  ) : (
                    <Input
                      placeholder="Placeholder (optional)"
                      value={q.placeholder || ''}
                      onChange={(e) => update(idx, 'placeholder', e.target.value)}
                      size="sm"
                    />
                  )}
                </div>
                {!locked ? (
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    className="rounded p-2 text-red-500 hover:bg-red-50"
                    title="Remove field"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={add}
        disabled={fields.length >= maxFields}
        className="inline-flex items-center gap-2 text-sm font-medium text-ink hover:underline disabled:opacity-50"
      >
        <Plus className="h-4 w-4" />
        Add custom field
      </button>
    </div>
  );
};

export default QuestionSchemaEditor;
